import jstz from 'jstz';
import {sessionFreeRoute} from '~/utils/sessionFreeRoutes';

/**
 * Detects user's timezone using multiple methods for maximum reliability
 * Priority order: jstz > Intl > offset-based > UTC fallback
 */
function detectTimezone() {
    // Method 1: Use jstz library (most reliable cross-browser solution)
    try {
        const timezone = jstz.determine();
        const timezoneName = timezone.name();

        if (timezoneName && timezoneName !== 'UTC') {
            // Validate the timezone is actually usable
            try {
                Intl.DateTimeFormat('en-US', { timeZone: timezoneName });
                console.log('Timezone detected via jstz:', timezoneName);
                return timezoneName;
            } catch (e) {
                console.warn('jstz returned invalid timezone:', timezoneName);
            }
        }
    } catch (e) {
        console.warn('jstz detection failed:', e);
    }

    // Method 2: Try Intl.DateTimeFormat as fallback
    try {
        const intlTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

        if (intlTimezone && intlTimezone !== 'UTC') {
            try {
                // Test if timezone is valid by trying to use it
                Intl.DateTimeFormat('en-US', { timeZone: intlTimezone });
                console.log('Timezone detected via Intl:', intlTimezone);
                return intlTimezone;
            } catch (e) {
                console.warn('Invalid timezone from Intl:', intlTimezone);
            }
        }
    } catch (e) {
        console.warn('Intl.DateTimeFormat failed:', e);
    }

    // Method 3: Use offset-based detection as fallback
    try {
        const offset = -new Date().getTimezoneOffset();
        const offsetHours = Math.floor(Math.abs(offset) / 60);
        const offsetMinutes = Math.abs(offset) % 60;
        const offsetSign = offset >= 0 ? '+' : '-';

        const offsetKey = `${offsetSign}${String(offsetHours).padStart(2, '0')}:${String(offsetMinutes).padStart(2, '0')}`;

        // Common timezone mappings based on offset
        const offsetToTimezone = {
            '-12:00': 'Pacific/Auckland',
            '-11:00': 'Pacific/Midway',
            '-10:00': 'Pacific/Honolulu',
            '-09:00': 'America/Anchorage',
            '-08:00': 'America/Los_Angeles',
            '-07:00': 'America/Denver',
            '-06:00': 'America/Chicago',
            '-05:00': 'America/New_York',
            '-04:00': 'America/Halifax',
            '-03:00': 'America/Sao_Paulo',
            '-02:00': 'Atlantic/South_Georgia',
            '-01:00': 'Atlantic/Azores',
            '+00:00': 'Europe/London',
            '+01:00': 'Europe/Paris',
            '+02:00': 'Europe/Athens',
            '+03:00': 'Europe/Moscow',
            '+04:00': 'Asia/Dubai',
            '+05:00': 'Asia/Karachi',
            '+05:30': 'Asia/Kolkata',
            '+06:00': 'Asia/Dhaka',
            '+07:00': 'Asia/Bangkok',
            '+08:00': 'Asia/Singapore',
            '+09:00': 'Asia/Tokyo',
            '+09:30': 'Australia/Darwin',
            '+10:00': 'Australia/Sydney',
            '+11:00': 'Pacific/Noumea',
            '+12:00': 'Pacific/Fiji',
        };

        const guessedTimezone = offsetToTimezone[offsetKey];
        if (guessedTimezone) {
            console.log('Timezone detected via offset:', guessedTimezone);
            return guessedTimezone;
        }
    } catch (e) {
        console.warn('Offset-based detection failed:', e);
    }

    // Method 4: Last resort - return UTC
    console.warn('All timezone detection methods failed, falling back to UTC');
    return 'UTC';
}

export default defineNuxtPlugin(() => {
    // The anonymous feedback page keeps nothing in the browser, this cookie included
    // (sessionFreeRoutes, Outreach v1 checkpoint 11 fix round 3). Nothing reads it on the way
    // there, and the next page load sets it as before.
    if (sessionFreeRoute(window.location.pathname)) return;

    const cookie = useCookie('timezone', {
        maxAge: 60 * 60 * 24 * 365, // 1 year
        sameSite: 'lax',
    });

    const currentTz = detectTimezone();

    // Set the cookie if missing or if timezone has changed since last visit
    if (!cookie.value || cookie.value !== currentTz) {
        console.log('Timezone detected:', currentTz);
        cookie.value = currentTz;
    }
});